/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { PLAYER_DETAILS_FIELD_SECTIONS } from '@sportninja/common/features/profile/player/playerDetails/constants/sections'
import {
  profileExtraSectionFields,
  profileExtraSectionHasEditableFields,
  type ProfileExtrasAudience,
} from '@sportninja/common/features/profile/player/playerDetails/utils/playerProfileExtras'
import type { PlayerDetailsFieldSectionKey } from '@sportninja/common/features/profile/player/playerDetails/types'
import DetailEditField from 'src/features/profile/player/playerDetails/components/DetailEditField'
import { PLAYER_DETAILS_SECTION_ICONS } from 'src/features/profile/player/playerDetails/constants/sectionItems'
import { FieldRow } from '../FormikField'
import EditableSection from '../EditableSection'

interface ProfileExtraSectionProps {
  /** One of `PROFILE_EXTRA_SECTIONS` — a Player Details field section. */
  sectionKey: PlayerDetailsFieldSectionKey
  /**
   * Show the display-only fields (e.g. Previous Years Played, taken from the
   * player's registrations). Off when adding a player, who has none yet.
   */
  showReadOnlyFields?: boolean
  /**
   * `self` (a player on their own profile) leaves out the admin-only fields;
   * `admin` (the roster editor) shows them all. Must match the audience the
   * form reads and saves with.
   */
  audience?: ProfileExtrasAudience
}

/**
 * A Player Details section inside the Edit Player forms, rendered from the
 * same definition (fields, options, icon) so both screens read the same. Reads
 * first, then edits, like every other section here.
 */
const ProfileExtraSection: React.FC<ProfileExtraSectionProps> = ({
  sectionKey,
  showReadOnlyFields = true,
  audience = 'admin',
}) => {
  const section = PLAYER_DETAILS_FIELD_SECTIONS[sectionKey]
  const fields = profileExtraSectionFields(sectionKey, audience).filter(
    (def) => showReadOnlyFields || !def.locked
  )
  const Icon = PLAYER_DETAILS_SECTION_ICONS[sectionKey]

  // Nothing for this audience, or only display fields when adding a player.
  if (fields.length === 0) {
    return null
  }

  return (
    <EditableSection
      id={sectionKey}
      title={capitalizeWords(section.label)}
      icon={<Icon />}
      editable={profileExtraSectionHasEditableFields(sectionKey, audience)}
    >
      <FieldRow cols={2}>
        {fields.map((def) => (
          <div key={def.name} css={def.fullWidth ? fullWidthStyle : cellStyle}>
            <DetailEditField def={def} />
          </div>
        ))}
      </FieldRow>
    </EditableSection>
  )
}

export default ProfileExtraSection

// ---- styled ----
const cellStyle = css`
  min-width: 0;
`

const fullWidthStyle = css`
  min-width: 0;
  grid-column: 1 / -1;
`
